import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal, { SweetAlertOptions } from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  applyCouponCode,
  applyGiftCard,
  removeCouponCode,
  removeGiftCard,
  removeServiceCharge,
  updateServiceCharge,
} from "../../../../../services/table.services";
import {
  fireToast as sharedFireToast,
  getErrorMessage as sharedGetErrorMessage,
  getGiftCardInlineError,
} from "../utils/toastHelpers.ts";
import type {
  DiscountFormValues,
  DiscountServiceResponse,
  ServiceChargeFormValues,
  UseAllTogetherDiscountsProps,
  UseAllTogetherDiscountsReturn,
} from "./types.ts";

const asServiceResponse = (response: unknown): DiscountServiceResponse =>
  response as DiscountServiceResponse;

export const useAllTogetherDiscounts = ({
  tableDetail,
  SWAL_SETTINGS,
  trash,
  onOrderChanged,
}: UseAllTogetherDiscountsProps): UseAllTogetherDiscountsReturn => {
  const { t } = useTranslation();
  const [giftCardApplied, setGiftCardApplied] = useState<boolean>(false);
  const [serviceChargeApplied, setServiceChargeApplied] = useState<boolean>(false);
  const [couponCodeApplied, setCouponCodeApplied] = useState<boolean>(false);
  const clearingRef = useRef(false);

  const fireToast = useCallback(
    (icon: SweetAlertOptions["icon"], message?: string): void =>
      sharedFireToast(t, SWAL_SETTINGS, icon, message),
    [t, SWAL_SETTINGS],
  );

  const getErrorMessage = useCallback(
    (error: unknown): string => sharedGetErrorMessage(t, error),
    [t],
  );

  const runDiscountMutation = useCallback(
    async ({
      request,
      onSuccess,
      errorLabel,
    }: {
      request: Promise<unknown>;
      onSuccess?: (response: DiscountServiceResponse) => void;
      errorLabel: string;
    }): Promise<void> => {
      try {
        const serviceResponse = asServiceResponse(await request);
        if (serviceResponse?.success) {
          onSuccess?.(serviceResponse);
          onOrderChanged();
          fireToast("success", serviceResponse.message);
          return;
        }

        fireToast("error", serviceResponse?.message);
      } catch (error: unknown) {
        console.error(errorLabel, error);
        fireToast("error", getErrorMessage(error));
      }
    },
    [fireToast, getErrorMessage, onOrderChanged],
  );

  const formik = useFormik<DiscountFormValues>({
    initialValues: {
      couponCode: "",
      gift_card_amount: "",
      isCouponChecked: false,
      isGiftCardChecked: false,
    },
    validationSchema: Yup.object({
      couponCode: Yup.string()
        .nullable()
        .test("couponCode", t("error_coupon_required"), function (value) {
          const { isCouponChecked } = this.parent as DiscountFormValues;
          return !isCouponChecked || (isCouponChecked && !!value && value.trim() !== "");
        }),
      gift_card_amount: Yup.string()
        .test("gift-card-validation", t("error_gift_card_required"), function (value) {
          const { isGiftCardChecked } = this.parent as DiscountFormValues;
          if (!isGiftCardChecked) return true;

          const errorMsg = getGiftCardInlineError(t, value);
          if (errorMsg) {
            return this.createError({ message: errorMsg });
          }

          return true;
        }),
    }),
    onSubmit: () => { },
  });

  const serviceChargeFormik = useFormik<ServiceChargeFormValues>({
    initialValues: {
      service_charge_percentage: 0,
      is_service_charge_percentage: false,
    },
    validationSchema: Yup.object({
      service_charge_percentage: Yup.number()
        .required(t("error_service_charge_required"))
        .min(0, t("number_err_msg"))
        .max(100, t("max_number_err_msg"))
        .integer(t("service_charge_negative_error")),
    }),
    onSubmit: () => { },
  });

  useEffect(() => {
    const activeOrder = tableDetail?.activeOrder;
    if (!activeOrder) return;

    if (activeOrder.gift_card_applied) {
      setGiftCardApplied(true);
      formik.setFieldValue("isGiftCardChecked", true);
      formik.setFieldValue(
        "gift_card_amount",
        activeOrder.gift_card_amount != null ? String(activeOrder.gift_card_amount) : "",
      );
    } else {
      setGiftCardApplied(false);
      formik.setFieldValue("isGiftCardChecked", false);
      formik.setFieldValue("gift_card_amount", "");
    }

    if (activeOrder.coupon_code_applied) {
      setCouponCodeApplied(true);
      formik.setFieldValue("isCouponChecked", true);
      formik.setFieldValue("couponCode", activeOrder.coupon_code ?? "");
    } else {
      setCouponCodeApplied(false);
      formik.setFieldValue("isCouponChecked", false);
      formik.setFieldValue("couponCode", "");
    }

    if (activeOrder.service_charge_percentage) {
      setServiceChargeApplied(true);
      serviceChargeFormik.setFieldValue("service_charge_percentage", activeOrder.service_charge_percentage);
      serviceChargeFormik.setFieldValue("is_service_charge_percentage", true);
    } else {
      setServiceChargeApplied(false);
      serviceChargeFormik.setFieldValue("service_charge_percentage", "");
      serviceChargeFormik.setFieldValue("is_service_charge_percentage", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik/serviceChargeFormik are stable Formik instances
  }, [tableDetail]);

  const applyGiftCardAmount = useCallback((): void => {
    void runDiscountMutation({
      request: applyGiftCard({
        order_id: tableDetail?.activeOrder?._id,
        gift_card_amount: Number(formik.values.gift_card_amount),
      }),
      onSuccess: () => {
        setGiftCardApplied(true);
      },
      errorLabel: "[useAllTogetherDiscounts] applyGiftCard failed",
    });
  }, [runDiscountMutation, tableDetail?.activeOrder?._id, formik.values.gift_card_amount]);

  const applyServiceCharge = useCallback((): void => {
    void runDiscountMutation({
      request: updateServiceCharge({
        order_id: tableDetail?.activeOrder?._id,
        updated_service_charge: serviceChargeFormik.values.service_charge_percentage,
      }),
      onSuccess: () => {
        setServiceChargeApplied(true);
      },
      errorLabel: "[useAllTogetherDiscounts] updateServiceCharge failed",
    });
  }, [runDiscountMutation, tableDetail?.activeOrder?._id, serviceChargeFormik.values.service_charge_percentage]);

  const removeServiceChargePercentage = useCallback((): void => {
    void runDiscountMutation({
      request: removeServiceCharge({ order_id: tableDetail?.activeOrder?._id }),
      onSuccess: () => {
        setServiceChargeApplied(false);
      },
      errorLabel: "[useAllTogetherDiscounts] removeServiceCharge failed",
    });
  }, [runDiscountMutation, tableDetail?.activeOrder?._id]);

  const handleServiceChargePercentageChange = useCallback((val: boolean): void => {
    if (!val && serviceChargeApplied) {
      void (async () => {
        const result = await Swal.fire({
          title: t("msg_are_you_sure"),
          text: t("msg_remove_service_charge"),
          imageUrl: trash,
          showCancelButton: true,
          confirmButtonColor: "#dc3545",
          cancelButtonColor: "#6c757d",
          confirmButtonText: t("btn_delete"),
          cancelButtonText: t("btn_cancel"),
          reverseButtons: true,
          customClass: {
            popup: "swal-restaurant",
            confirmButton: "btn btn-danger col",
            cancelButton: "btn btn-secondary col",
          },
          allowOutsideClick: false,
        });

        if (!result.isConfirmed) return;

        removeServiceChargePercentage();
        serviceChargeFormik.setFieldValue("is_service_charge_percentage", false);
        serviceChargeFormik.setFieldValue("service_charge_percentage", "");
      })();
      return;
    }

    serviceChargeFormik.setFieldValue("is_service_charge_percentage", val);
  }, [serviceChargeApplied, t, trash, removeServiceChargePercentage, serviceChargeFormik]);

  const removeGiftCardAmount = useCallback((): void => {
    void runDiscountMutation({
      request: removeGiftCard({ order_id: tableDetail?.activeOrder?._id }),
      onSuccess: () => {
        setGiftCardApplied(false);
      },
      errorLabel: "[useAllTogetherDiscounts] removeGiftCard failed",
    });
  }, [runDiscountMutation, tableDetail?.activeOrder?._id]);

  const resetGiftCardField = useCallback((): void => {
    removeGiftCardAmount();
    formik.setFieldValue("isGiftCardChecked", false);
    formik.setFieldValue("gift_card_amount", "");
  }, [removeGiftCardAmount, formik]);

  const handleGiftCardChange = useCallback((val: boolean): void => {
    if (!val && giftCardApplied) {
      resetGiftCardField();
      return;
    }
    formik.setFieldValue("isGiftCardChecked", val);
  }, [giftCardApplied, resetGiftCardField, formik]);

  const applyCouponCodeAmount = useCallback((): void => {
    if (!formik.values.couponCode || formik.values.couponCode.trim() === "") return;

    void runDiscountMutation({
      request: applyCouponCode({
        order_id: tableDetail?.activeOrder?._id,
        coupon_code: formik.values.couponCode,
      }),
      onSuccess: () => {
        setCouponCodeApplied(true);
      },
      errorLabel: "[useAllTogetherDiscounts] applyCouponCode failed",
    });
  }, [formik.values.couponCode, runDiscountMutation, tableDetail?.activeOrder?._id]);

  const removeCouponCodeAmount = useCallback((): void => {
    void runDiscountMutation({
      request: removeCouponCode({ order_id: tableDetail?.activeOrder?._id }),
      onSuccess: () => {
        setCouponCodeApplied(false);
      },
      errorLabel: "[useAllTogetherDiscounts] removeCouponCode failed",
    });
  }, [runDiscountMutation, tableDetail?.activeOrder?._id]);

  const handleCouponCodeChange = useCallback((val: boolean): void => {
    if (!val && couponCodeApplied) {
      removeCouponCodeAmount();
      formik.setFieldValue("isCouponChecked", false);
      formik.setFieldValue("couponCode", "");
      return;
    }
    formik.setFieldValue("isCouponChecked", val);
  }, [couponCodeApplied, removeCouponCodeAmount, formik]);

  const clearAllTogetherDiscounts = useCallback(async (): Promise<boolean> => {
    const orderId = tableDetail?.activeOrder?._id;
    if (!orderId) return true;
    if (clearingRef.current) return false;

    const clearOps: { type: "coupon" | "giftCard"; request: Promise<unknown> }[] = [];

    if (couponCodeApplied) {
      clearOps.push({ type: "coupon", request: removeCouponCode({ order_id: orderId }) });
    }

    if (giftCardApplied) {
      clearOps.push({ type: "giftCard", request: removeGiftCard({ order_id: orderId }) });
    }

    if (!clearOps.length) return true;

    clearingRef.current = true;
    try {
      const settled = await Promise.allSettled(clearOps.map((op) => op.request));
      let allSucceeded = true;

      settled.forEach((outcome, index) => {
        const succeeded = outcome.status === "fulfilled" && !!asServiceResponse(outcome.value)?.success;
        if (!succeeded) {
          allSucceeded = false;
          return;
        }

        // Clear only the flags for operations that actually succeeded server-side,
        // so a partial failure doesn't leave local state claiming a discount is
        // still applied when the server already removed it (or vice versa).
        if (clearOps[index].type === "coupon") {
          setCouponCodeApplied(false);
          formik.setFieldValue("isCouponChecked", false);
          formik.setFieldValue("couponCode", "");
        } else {
          setGiftCardApplied(false);
          formik.setFieldValue("isGiftCardChecked", false);
          formik.setFieldValue("gift_card_amount", "");
        }
      });

      if (!allSucceeded) {
        fireToast("error", "something_went_wrong");
        return false;
      }

      onOrderChanged();
      return true;
    } catch (error: unknown) {
      console.error("[useAllTogetherDiscounts] clearAllTogetherDiscounts failed", error);
      fireToast("error", getErrorMessage(error));
      return false;
    } finally {
      clearingRef.current = false;
    }
  }, [
    tableDetail?.activeOrder?._id,
    couponCodeApplied,
    giftCardApplied,
    fireToast,
    formik,
    onOrderChanged,
    getErrorMessage,
  ]);

  return {
    formik,
    serviceChargeFormik,
    giftCardApplied,
    serviceChargeApplied,
    couponCodeApplied,
    clearAllTogetherDiscounts,
    applyGiftCardAmount,
    applyServiceCharge,
    handleServiceChargePercentageChange,
    handleGiftCardChange,
    applyCouponCodeAmount,
    handleCouponCodeChange,
  };
};
